import { readPointUserDetailList, readPointUserDetailInfo, readPointUserLedgerTotal } from '@/api/point/read';
import { packBigNumbers } from '@/lib/BigNumbers';
import {
  IdsType,
  ResReadPointUserDetailType,
  ResReadPointUserLedgerListType,
  UpdatePointCustomReferralType,
  UpdatePointUserIdAddressType,
} from '@/types';
import { ChangeEvent, useEffect, useState, MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Button, Card, CardContent, Divider, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import Header from '@/component/Header';
import Grid from '@mui/material/Unstable_Grid2';
import { Tables } from '@/component/pools/table/Tables';
import { pointUserDetailListColumns } from '@/component/point/table/ColumnData';
import { pointLedgerStatusFilter } from '@/lib/filter/points';
import { updatePointCustomReferral, updatePointIdAddress } from '@/api/point/update';
import { SUCCESS } from '@/lib/common/name-space';
import { deletePointUsers } from '@/api/point/create';

export default function PointUserDetail() {
  const {
    state: { pointId },
  } = useLocation();

  const [detail, setDetail] = useState<ResReadPointUserDetailType | null>(null);
  const [oldDetail, setOldDetail] = useState<ResReadPointUserDetailType | null>(detail);
  const [detailList, setDetailList] = useState<ResReadPointUserLedgerListType[] | []>([]);
  const [ledgerTotal, setLedgerTotal] = useState<{ status: number; amount: string }[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [resInfo, resList, resTotal] = await Promise.allSettled([
        readPointUserDetailInfo(pointId),
        readPointUserDetailList(pointId),
        readPointUserLedgerTotal(pointId),
      ]);

      // 각 결과의 status와 value를 확인
      if (
        resInfo.status !== 'fulfilled' ||
        resList.status !== 'fulfilled' ||
        resInfo.value.status !== 200 ||
        resList.value.status !== 200 ||
        resTotal.status !== 'fulfilled' ||
        resTotal.value.status !== 200
      ) {
        toast.error('불러오기 실패');
        return;
      }
      setDetail(resInfo.value.data);
      setOldDetail(resInfo.value.data);
      const list = resList.value.data.map((item: ResReadPointUserLedgerListType, index: number) => ({
        ...item,
        index,
        amount: packBigNumbers(item.amount.toString()),
        status: pointLedgerStatusFilter(item.status),
      }));
      setDetailList(list);
      setLedgerTotal(resTotal.value.data);
      // resTotal는 기존 코드에서 사용하지 않으므로 필요시 추가 사용
    })();
    return () => {};
  }, []);

  // useEffect(() => {
  //   (async () => {
  //     const res = await readPointUserDetailInfo(pointId);
  //     const re = await readPointUserDetailList(pointId);
  //     const result = await readPointUserLedgerTotal(pointId);

  //     if (re.status !== 200 || res.status !== 200) {
  //       toast.error('불러오기 실패');
  //       return;
  //     }
  //     setDetail(res.data);
  //     setOldDetail(res.data);
  //     const list = re.data.map((item: ResReadPointUserLedgerListType, index: number) => ({
  //       ...item,
  //       index,
  //       amount: packBigNumbers(item.amount.toString()),
  //       status: pointLedgerStatusFilter(item.status),
  //     }));
  //     setDetailList(list);
  //   })();
  //   return () => {};
  // }, []);

  const updateReferral = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    console.log(name);
    console.log(value);
    if (name !== 'customReferralCnt') return;
    setDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [name]: value,
      };
    });
  };
  const onSubmitUpdate = async () => {
    if (!detail?.userId || detail.customReferralCnt === undefined) {
      return;
    }
    const updatePointCustomReferralType: UpdatePointCustomReferralType = {
      userId: detail?.userId,
      customReferralCnt: detail?.customReferralCnt,
    };
    const re = await updatePointCustomReferral(updatePointCustomReferralType);
    if (re.status !== SUCCESS) {
      toast.error('커스텀 레퍼럴 업데이트 실패');
      return;
    }
    toast.success('커스텀 레퍼럴 업데이트 성공');
  };

  const handleChangeValue = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    console.log(`name: ${name}, value: ${value}`);
    setDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [name]: value,
      };
    });
  };
  const handleUpdateField = async (field: 'id' | 'address') => {
    if (!detail || !oldDetail) return;
    if (detail[field] === oldDetail[field]) return;

    try {
      await updatePointIdAddress({ userId: detail.userId, [field]: detail[field] });
      toast.success(`${field} 변경 성공`);

      if (field === 'address') {
        setTimeout(() => navigate('/point-user-list'), 2000);
      }
    } catch {
      toast.error(`${field} 변경 실패`);
    }
  };

  const onSubmitDelete = async () => {
    const idsType: IdsType = {
      userId: detail!.userId,
      settingId: detail!.settingId,
      pointId: detail!.pointId,
    };
    try {
      await deletePointUsers(idsType);
      toast.success('성공');
    } catch (e) {
      toast.error('실패');
    }
  };
  return (
    <>
      <Box sx={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
        {detail && (
          <Card>
            <Header title="사용자 상세 정보" />
            <Divider />
            <CardContent
              sx={{
                flexGrow: 1, // 남은 공간 채움
                overflowY: 'auto',
                p: 2,
                backgroundColor: '#f9f9f9',
              }}
            >
              <Grid container spacing={3}>
                <Grid md={4} xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>id</InputLabel>
                    <OutlinedInput
                      type="text"
                      value={detail.id}
                      label="id"
                      name="id"
                      onChange={(e) => handleChangeValue(e)}
                    />
                  </FormControl>
                </Grid>
                <Grid md={2} xs={12}>
                  <Button onClick={() => handleUpdateField('id')}>아이디 변경</Button>
                </Grid>

                <Grid md={6} xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>추천인 코드</InputLabel>
                    <OutlinedInput type="text" value={detail.referralCode} label="referralCode" name="referralCode" />
                  </FormControl>
                </Grid>
              </Grid>
              <Box mb={3} />

              <Grid container spacing={3}>
                <Grid md={8} xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>address</InputLabel>
                    <OutlinedInput
                      type="text"
                      value={detail.address}
                      label="address"
                      name="address"
                      onChange={(e) => handleChangeValue(e)}
                    />
                  </FormControl>
                </Grid>
                <Grid md={4} xs={12}>
                  <Button onClick={() => handleUpdateField('address')}>address 변경</Button>
                </Grid>
              </Grid>
              <Box mb={3} />

              <Grid container spacing={3}>
                <Grid xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>총 획득한 포인트 합산</InputLabel>
                    <OutlinedInput type="text" value={packBigNumbers(detail.total)} label="total" name="total" />
                  </FormControl>
                </Grid>
              </Grid>
              <Box mb={3} />

              <Grid container spacing={3}>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>채굴 포인트 합산</InputLabel>
                    <OutlinedInput
                      type="text"
                      value={packBigNumbers(detail.totPoint)}
                      label="totPoint"
                      name="totPoint"
                    />
                  </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>채굴 횟수</InputLabel>
                    <OutlinedInput
                      type="text"
                      value={packBigNumbers(detail.totCount)}
                      label="totCount"
                      name="totCount"
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box mb={3} />

              <Grid container spacing={3}>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>추천인 포인트 합산</InputLabel>
                    <OutlinedInput
                      type="text"
                      value={detail.totReferralPoint}
                      label="totReferralPoint"
                      name="totReferralPoint"
                    />
                  </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>추천인 횟수</InputLabel>
                    <OutlinedInput
                      type="text"
                      value={detail.totReferralCount}
                      label="totReferralCount"
                      name="totReferralCount"
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box mb={3} />

              <Grid container spacing={3}>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>보너스 포인트 합산</InputLabel>
                    <OutlinedInput
                      type="text"
                      value={packBigNumbers(detail.totBonusPoint)}
                      label="totBonusPoint"
                      name="totBonusPoint"
                    />
                  </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>보너스 횟수</InputLabel>
                    <OutlinedInput
                      type="text"
                      value={packBigNumbers(detail.totBonusCount)}
                      label="totBonusCount"
                      name="totBonusCount"
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box mb={3} />
              <Grid container spacing={3}>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>커스텀 레퍼럴 카운트 </InputLabel>
                    <OutlinedInput
                      type="text"
                      value={detail.customReferralCnt}
                      label="customReferralCnt"
                      name="customReferralCnt"
                      onChange={updateReferral}
                    />
                  </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                  <Button variant="contained" onClick={() => onSubmitUpdate()}>
                    커스텀 레퍼럴만 변경
                  </Button>
                </Grid>
              </Grid>

              <Box mb={3} />
              <Grid container spacing={3}>
                <Grid md={10}>※ 포인트가 0일 때만 가능합니다.</Grid>
                <Grid md={2} xs={12}>
                  <Button variant="contained" color="error" onClick={onSubmitDelete}>
                    아이디 삭제
                  </Button>
                </Grid>
              </Grid>
              {ledgerTotal.length > 0 && (
                <>
                  <Box mb={5} />
                  원장 합산
                  <Box mb={2} />
                  {ledgerTotal.map((item, idx) => (
                    <>
                      <Grid container spacing={3}>
                        <Grid md={6} xs={12} key={idx}>
                          <FormControl fullWidth required sx={{ mb: 2 }}>
                            <InputLabel>{pointLedgerStatusFilter(item.status)}</InputLabel>
                            <OutlinedInput
                              type="text"
                              value={item.amount}
                              label="amount"
                              name="amount"
                              // onChange 필요시 추가
                              readOnly
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                    </>
                  ))}
                  <Box mb={3} />
                </>
              )}
            </CardContent>
          </Card>
        )}

        {detailList.length > 0 && (
          <CardContent sx={{ height: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            원장 리스트
            {detailList && <Tables list={detailList} columns={pointUserDetailListColumns} />}
          </CardContent>
        )}
      </Box>
    </>
  );
}
